import { useState } from "react";
import { Pager } from "@/app/Pager";
import { AppendDrawer } from "@/app/AppendDrawer";
import { channels, suppliers, CHARGE_ON, costPrices, type Channel, type Supplier, type CostPrice, type Input } from "@/app/master";
import { loadingOf, nameOf, useList } from "@/app/queries";
import { usePage } from "@/app/usePage";
import { Alert, EmptyState, formatMoney, formatTime, Pill, Table, TableWrap, Th } from "@/ui";
import { costDraft, costInput, lockedChargeOn } from "./costDraft";
import { useCanWrite } from "@/app/auth";
import { listedPrices, usePriceClock } from "./priceList";
import { CostPriceForm } from "./CostForms";

import { PriceFilters } from "./PriceFilters";
import { EMPTY_PRICE_FILTERS, matchesPriceFilters } from "./priceFilterState";

/** 成本价页签：按供应商、通道、MCC/MNC 筛选，列表优先显示当前价格，仅有未来价格时显示最近一条，修改进入时间线详情（D113）。 */
export function CostPrices({ adding, onCloseAdd }: { adding: boolean; onCloseAdd: () => void }) {
  const [filters, setFilters] = useState(EMPTY_PRICE_FILTERS);
  const channelId = filters.entityId;
  const now = usePriceClock();
  const canWrite = useCanWrite();
  const supplierList = useList("suppliers", suppliers);
  const channelList = useList("channels", channels);
  const list = useList("cost-prices", costPrices, channelId ? { channel_id: channelId } : undefined);
  const rows = listedPrices(list.data ?? [], (p) => p.channel_id, now).filter((p) => matchesPriceFilters(filters, p, p.channel_id, channelList.data?.find((c) => c.id === p.channel_id)?.supplier_id));
  const paged = usePage("cost-prices", rows, [filters]);
  return (
    <>
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <PriceFilters ownerLabel="供应商" entityLabel="通道" entitySearch="搜索通道名称或 ID"
        owners={supplierList.data} entities={channelList.data?.map((c) => ({ ...c, ownerId: c.supplier_id }))}
        value={filters} onChange={setFilters} />
      <TableWrap loading={loadingOf(list)}>
        {rows.length ? (
          <>
            <Table>
              <thead><tr><Th w="id">ID</Th><Th w="name">通道</Th><Th w="name">供应商</Th><Th w="code">MCC</Th><Th w="code">MNC</Th><Th numeric w="money">价格</Th><Th w={96}>计费类型</Th><Th numeric w={224}>生效时间</Th><Th w={120}>操作者</Th><Th /></tr></thead>
              <tbody>
                {paged.rows.map((p) => (
                  <tr key={p.id}>
                    <td className="ax-mono">{p.id}</td><td>{nameOf(channelList.data, p.channel_id)}</td><td>{nameOf(supplierList.data, channelList.data?.find((c) => c.id === p.channel_id)?.supplier_id ?? 0)}</td><td className="ax-mono">{p.mcc}</td><td className="ax-mono">{p.mnc ?? "全部"}</td>
                    <td className="ax-num"><span className="ax-money">{formatMoney(p.price, p.currency, 6)}</span></td><td>{CHARGE_ON[p.charge_on]}</td><td className="ax-num ax-mono">{formatTime(p.effective_from, false)}{Date.parse(p.effective_from) > now && <> <Pill tone="neutral">待生效</Pill></>}</td>
                    <td className="ax-secondary">{p.created_by}</td><td><a className="ax-link" href={`/prices/cost/${p.id}`}>{canWrite ? "编辑" : "详情"}</a></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !list.isPending && <EmptyState title="没有通道价格">没有通道价格的通道路由时不计成本，利润保护也算不出它的亏损。</EmptyState>
        )}
      </TableWrap>
      {adding && <CostDrawer channelId={channelId} channels={channelList.data} suppliers={supplierList.data} onClose={onCloseAdd} />}
    </>
  );
}

function CostDrawer({ channelId, channels: channelRows, suppliers: supplierRows, onClose }: { channelId: string; channels?: Channel[]; suppliers?: Supplier[]; onClose: () => void }) {
  const channel = channelRows?.find((c) => String(c.id) === channelId);
  const supplierId = channel?.supplier_id;
  return (
    <AppendDrawer
      noun="通道价格"
      initial={costDraft(channelId, supplierId ? String(supplierId) : "", lockedChargeOn(channel))}
      convert={costInput}
      save={(body: Input<CostPrice>) => costPrices.create(body)}
      invalidate={["cost-prices", "price-sync-tasks"]}
      message={(p) => p.sync_task_id ? `已新增通道价格，价格同步任务 #${p.sync_task_id} 已排队` : "已新增通道价格"}
      note="保存价格并发布配置；涉及历史变化时自动创建串行同步任务。此操作写入审计日志。"
      summary={(body) => [
        { label: "供应商", value: nameOf(supplierRows, channelRows?.find((c) => c.id === body.channel_id)?.supplier_id ?? 0) },
        { label: "通道", value: nameOf(channelRows, body.channel_id) },
        { label: "目的地", value: `${body.mcc}-${body.mnc ?? "全部"}`, mono: true },
        { label: "价格", value: <span className="ax-money">{formatMoney(body.price, body.currency, 6)}</span> },
        { label: "计费类型", value: CHARGE_ON[body.charge_on] },
        { label: "生效时间", value: `${formatTime(body.effective_from)} 北京时间`, mono: true },
      ]}
      onClose={onClose}
    >
      {(form) => <CostPriceForm draft={form.draft} errors={form.errors} set={form.set} channels={channelRows} suppliers={supplierRows} />}
    </AppendDrawer>
  );
}
